/**
 * FleaMarketPreviewCard – live preview mimicking the public flea market card.
 * Mirrors the pattern used by ActivityComposer's previewCard.
 */

import { CalendarDays, ImageIcon, MapPin } from 'lucide-react';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';

interface FleaMarketPreviewCardProps {
  title: string;
  description: string | null;
  fleaMarketId: string | null | undefined;
  nextOccurrenceLabel: string | null;
}

export function FleaMarketPreviewCard({
  title,
  description,
  fleaMarketId,
  nextOccurrenceLabel,
}: FleaMarketPreviewCardProps) {
  const coverUrl = useCoverImageUrl('flea_market', fleaMarketId, 640);

  const trimmedDesc = (() => {
    if (!description) return null;
    const plain = description.replace(/<[^>]*>/g, '').trim();
    if (!plain) return null;
    return plain.length > 100 ? plain.slice(0, 100) + '…' : plain;
  })();

  return (
    <div className="rounded-xl overflow-hidden border border-border/40 bg-card shadow-sm">
      {/* Image area */}
      <div className="aspect-[16/9] bg-muted relative overflow-hidden">
        {coverUrl ? (
          <img src={coverUrl} alt="Förhandsvisning" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="rounded-full bg-muted-foreground/[0.06] p-4">
              <ImageIcon className="h-6 w-6 text-muted-foreground/30" />
            </div>
            <p className="text-[11px] text-muted-foreground/40">Omslagsbild visas här</p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pt-3 pb-4 space-y-2">
        <h3 className="font-display font-semibold text-[15px] text-foreground leading-snug">
          {title || 'Din loppis'}
        </h3>

        {/* Next occurrence */}
        <div className="flex items-center gap-1.5 text-sm">
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
          {nextOccurrenceLabel ? (
            <span className="text-muted-foreground">{nextOccurrenceLabel}</span>
          ) : (
            <span className="text-muted-foreground/50 italic">Inga tillfällen ännu</span>
          )}
        </div>

        {/* Description excerpt */}
        {trimmedDesc && (
          <p className="text-xs text-muted-foreground/70 leading-relaxed line-clamp-2">{trimmedDesc}</p>
        )}
      </div>
    </div>
  );
}
